import type { GrantMatch, GrantReport } from './lookups';

const usdCompact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  // Explicit minimum: currency style defaults it to 2, and engines disagree on how that clamps
  // against the max — some render "$500.0K". Pinning both keeps "$500K" / "$1.3M" everywhere.
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

export function formatUsdCompact(amount: number): string {
  return usdCompact.format(amount);
}

function formatIsoDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole days from today (UTC) until an ISO date; negative once it has passed. */
export function daysUntil(iso: string, now: Date = new Date()): number {
  const [y, m, d] = iso.split('-').map(Number);
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((Date.UTC(y, m - 1, d) - today) / DAY_MS);
}

/** "Closes Nov 10 · 32 days left" / "Opening soon" / null when nothing is posted. */
export function grantStatusLabel(match: Pick<GrantMatch, 'status' | 'dueDate'>, now: Date = new Date()): string | null {
  if (match.status === 'open') {
    if (!match.dueDate) return 'Open now';
    const days = daysUntil(match.dueDate, now);
    const left = days === 0 ? 'closes today' : days === 1 ? '1 day left' : `${days} days left`;
    return days >= 0 ? `Closes ${formatIsoDate(match.dueDate)} · ${left}` : 'Open now';
  }
  if (match.status === 'forecasted') return match.dueDate ? `Opening soon · est. due ${formatIsoDate(match.dueDate)}` : 'Opening soon';
  return null;
}

/** The "why this grant" line: who like you got it, or that it's open to districts like you. */
export function grantReason(match: GrantMatch, peersCompared: number): string {
  if (match.source === 'open-opportunity') return 'Open to school districts like yours';
  const [first, second] = match.examplePeers.map((p) => p.districtName);
  const count = match.peerCount ?? 0;
  const amount = match.peerTotalAmount ? ` (${formatUsdCompact(match.peerTotalAmount)} total)` : '';
  if (first && count === 1) return `${first} received this — your district didn't${amount}`;
  if (first && second && count === 2) return `${first} and ${second} received this — your district didn't${amount}`;
  if (first) return `${first} and ${count - 1} other similar districts received this${amount}`;
  return `${count} of ${peersCompared} similar districts received this${amount}`;
}

export function grantAwardLabel(match: Pick<GrantMatch, 'awardCeiling' | 'awardRange'>): string | null {
  if (match.awardCeiling) return `Up to ${formatUsdCompact(match.awardCeiling)}`;
  return null;
}

export type ReportHeadline =
  | { kind: 'per-pupil'; anchorPerPupil: number; peerMedianPerPupil: number }
  | { kind: 'peer-dollars'; total: number; peerCount: number; programCount: number }
  | { kind: 'open-count'; count: number };

/** Below this share of the peer median, the per-student gap is the story. */
const PER_PUPIL_GAP_RATIO = 0.9;
/** Below this share, the gap is more likely an incomplete award match than real underfunding.
 * The benchmark already requires both direct and pass-through awards to resolve, but FSRS
 * reporting is uneven by state; never lead with a number a facilities director knows is wrong. */
const PER_PUPIL_PLAUSIBLE_RATIO = 0.25;

/**
 * Pick the strongest honest headline for a report:
 *   1. the district brings in clearly (but plausibly) less federal grant money per student;
 *   2. otherwise, the dollars similar districts won from programs this one hasn't used;
 *   3. otherwise, how many grants are open to it right now.
 */
export function reportHeadline(report: Pick<GrantReport, 'benchmark' | 'matches'>): ReportHeadline {
  const { anchorPerPupil, peerMedianPerPupil } = report.benchmark;
  if (
    anchorPerPupil != null &&
    peerMedianPerPupil != null &&
    peerMedianPerPupil > 0 &&
    anchorPerPupil < peerMedianPerPupil * PER_PUPIL_GAP_RATIO &&
    anchorPerPupil >= peerMedianPerPupil * PER_PUPIL_PLAUSIBLE_RATIO
  ) {
    return { kind: 'per-pupil', anchorPerPupil, peerMedianPerPupil };
  }
  const gaps = report.matches.filter((m) => m.source === 'peer-gap' && (m.peerTotalAmount ?? 0) > 0);
  if (gaps.length > 0) {
    return {
      kind: 'peer-dollars',
      total: gaps.reduce((sum, m) => sum + (m.peerTotalAmount ?? 0), 0),
      peerCount: new Set(gaps.flatMap((m) => m.peerDistrictIds)).size,
      programCount: gaps.length,
    };
  }
  return { kind: 'open-count', count: report.matches.length };
}

export function formatUsdWhole(amount: number): string {
  return `$${Math.round(amount).toLocaleString('en-US')}`;
}

/** Email the visitor can forward to their superintendent. */
export function shareEmail(report: Pick<GrantReport, 'districtName' | 'benchmark' | 'matches'>, url: string): { subject: string; body: string } {
  const headline = reportHeadline(report);
  const hook =
    headline.kind === 'per-pupil'
      ? `similar districts bring in ${formatUsdWhole(headline.peerMedianPerPupil)} per student each year in federal grants, and we bring in ${formatUsdWhole(headline.anchorPerPupil)}`
      : headline.kind === 'peer-dollars'
        ? `similar districts won ${formatUsdCompact(headline.total)} from grant programs we haven't used`
        : `there are ${headline.count} grants we can apply for right now`;
  return {
    subject: `Grants we may be missing — ${report.districtName}`,
    body: `I ran ${report.districtName} through FMX's grant finder: ${hook}.\n\nHere's the report: ${url}`,
  };
}

/** One-line plain-text version of the headline, for page titles and social cards. */
export function reportHeadlineText(report: Pick<GrantReport, 'districtName' | 'benchmark' | 'matches'>): string {
  const headline = reportHeadline(report);
  if (headline.kind === 'per-pupil') {
    return `Similar districts bring in ${formatUsdWhole(headline.peerMedianPerPupil)} per student each year in federal grants. ${report.districtName} brings in ${formatUsdWhole(headline.anchorPerPupil)}.`;
  }
  if (headline.kind === 'peer-dollars') {
    return `${formatUsdCompact(headline.total)} went to districts like ${report.districtName} through grant programs it hasn't used.`;
  }
  return `${headline.count} ${headline.count === 1 ? 'grant' : 'grants'} ${report.districtName} can go after right now.`;
}

function formatMonthYear(iso: string): string {
  const [y, m] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

export interface BondSummary {
  peersLine: string | null;
  anchorLine: string;
  /** Peers that passed a bond, for lighting up the map. */
  passedPeerIds: string[];
}

/**
 * The bond layer in two plain sentences: what similar districts passed, and where this
 * district stands. Null when there's nothing worth saying (no peer bonds and none of its own).
 */
export function bondSummary(report: Pick<GrantReport, 'districtName' | 'bonds'>): BondSummary | null {
  const layer = report.bonds;
  if (!layer) return null;
  const passed = layer.peers.filter((p) => p.totalPassed > 0);
  if (passed.length === 0 && !layer.anchor) return null;

  const total = passed.reduce((s, p) => s + p.totalPassed, 0);
  const peersLine =
    passed.length > 0
      ? `${passed.length} of ${layer.peersCovered} similar districts passed school bonds in the last ${layer.windowYears} years, ${formatUsdCompact(total)} in all.`
      : null;

  const name = report.districtName;
  const a = layer.anchor;
  let anchorLine: string;
  if (!a) {
    anchorLine = `${name} has no bond elections on record in that time.`;
  } else if (a.lastPassed) {
    const amount = a.lastPassed.amount ? ` (${formatUsdCompact(a.lastPassed.amount)})` : '';
    anchorLine = `${name} last passed one in ${formatMonthYear(a.lastPassed.date)}${amount}.`;
    if (a.last && a.last.passed === false && a.last.date > a.lastPassed.date) {
      anchorLine += ` Its most recent attempt, in ${formatMonthYear(a.last.date)}, didn't pass.`;
    }
  } else if (a.last?.passed === false) {
    const amount = a.last.amount ? ` for ${formatUsdCompact(a.last.amount)}` : '';
    anchorLine = `${name}'s last bond attempt${amount}, in ${formatMonthYear(a.last.date)}, didn't pass.`;
  } else {
    // Only cancelled or unreported questions on record — don't claim a defeat we can't see.
    anchorLine = `${name} hasn't passed a bond in that time.`;
  }

  return { peersLine, anchorLine, passedPeerIds: passed.map((p) => p.districtId) };
}
